<script setup lang="ts">
//#region imports
import { useRoute } from 'vue-router';
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import { isEmpty } from 'lodash-es';
import { format } from 'date-fns';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useLs } from '@/setup/vueLs';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useDatePickerShortcuts } from '@/composables/useDatePickerShortcuts';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import _ from '@/setup/lodash';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import updateUrl from '@/service/updateUrl';
import type { UiTableNewSortEmit } from '@/types/table';
import type {
  GetMediaBuyingReportsSummaryResponse,
  GetMediaBuyingReportsSummaryData,
} from '@/types/openapi-ts';
//#endregion

//#region types
type MediaBuyingReportRow = GetMediaBuyingReportsSummaryResponse['payload']['data'][number];
//#endregion

//#region const
const LS_PATH = 'MediaBuying/SummaryReport';

const sizes = {
  1080: 20,
  1440: 30,
};

const group_col = ref('affiliate_email');

const fields = computed(() => {
  return [
    {
      name: group_col.value,
      align: 'left',
      minWidth: '160',
      sortable: 'custom',
    },
    {
      name: 'balance_sum_approved',
      align: 'left',
      format: 'formatMoney',
    },
    {
      name: 'balance_sum_total',
      align: 'left',
      format: 'formatMoney',
    },
    {
      name: 'paid_sum_approved',
      align: 'left',
      format: 'formatMoney',
    },
    {
      name: 'paid_sum_pending',
      align: 'left',
      format: 'formatMoney',
    },
    {
      name: 'spent_sum_approved',
      align: 'left',
      format: 'formatMoney',
    },
    {
      name: 'spent_sum_pending',
      align: 'left',
      format: 'formatMoney',
    },
    {
      name: 'registrations_count',
      align: 'left',
      format: 'number',
    },
    {
      name: 'first_deposits_count',
      align: 'left',
      format: 'number',
    },
    {
      name: 'deposits_sum',
      align: 'left',
      format: 'formatMoney',
    },
    {
      name: 'ggr_sum',
      align: 'left',
      format: 'formatMoney',
      computedClass: getCurrencyClass,
    },
    {
      name: 'ngr_sum',
      align: 'left',
      format: 'formatMoney',
      computedClass: getCurrencyClass,
    },
    {
      name: 'net_project_profit_sum',
      align: 'left',
      format: 'formatMoney',
      computedClass: getCurrencyClass,
    },
    {
      name: 'cost_per_player',
      align: 'left',
      format: 'formatMoney',
    },
  ];
});
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const ls = useLs();
const route = useRoute();
const { checkCurrencyCode } = useFormatCurrency();
const { getPickerReportOptions, checkPeriod } = useDatePickerShortcuts();
const { checkRequest, checkRoute } = useDetectPermissions();

const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'affiliate_email',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields: fields.value,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region state
const group_by = ref('affiliate_id');

const nextGrouping = ref('');
const affiliate_id = ref('');
const root_campaign_id = ref('');
const campaign_id = ref('');
const affiliate_email = ref('');
const root_campaign_name = ref('');
const campaign_name = ref('');
const disableDrilldown = ref(true);
const blockedSearch = ref(false);
const selectedRow = ref<MediaBuyingReportRow>({} as MediaBuyingReportRow);

const currencyCode = ref<string | undefined>('');

const dateRange = ref<any[]>([]);
const datePeriod = ref('');
const dataTableRef = useTemplateRef('dataTableRef');

const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<MediaBuyingReportRow[]>([]);
//#endregion

//#region computed
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
//#endregion

//#region methods
function getCurrencyClass(value: number) {
  return value < 0 ? 'negative' : '';
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

function changeCurrency(value: string) {
  currencyCode.value = value;
  ls.set(`${LS_PATH}/currency_code`, value);
  fetchData();
}

async function handlerDrilldown() {
  blockedSearch.value = true;
  searchQuery.value = '';
  if (group_by.value === 'affiliate_id') {
    affiliate_email.value = selectedRow.value.affiliate_email || '';
    affiliate_id.value = selectedRow.value.affiliate_id || '';
    nextGrouping.value = 'root_campaign_id';
    group_col.value = 'root_campaign_name';
  }
  if (group_by.value === 'root_campaign_id') {
    root_campaign_name.value = selectedRow.value.root_campaign_name || '';
    root_campaign_id.value = selectedRow.value.root_campaign_id || '';
    nextGrouping.value = 'campaign_id';
    group_col.value = 'campaign_name';
  }
  if (group_by.value === 'campaign_id') {
    campaign_name.value = selectedRow.value.campaign_name || '';
    campaign_id.value = selectedRow.value.campaign_id || '';
    nextGrouping.value = 'xz';
  }

  if (nextGrouping.value === 'root_campaign_id') {
    group_by.value = nextGrouping.value;
    sortProp.value = sortProp.value === 'affiliate_email'
      ? 'root_campaign_name'
      : sortProp.value;
  }
  if (nextGrouping.value === 'campaign_id') {
    group_by.value = nextGrouping.value;
    sortProp.value = sortProp.value === 'root_campaign_name'
      ? 'campaign_name'
      : sortProp.value;
  }
  try {
    await nextTick();
    await fetchData();
  } finally {
    setTimeout(() => blockedSearch.value = false, 700);
  }
}

async function clearDrill(action: string) {
  if (action === 'all') {
    root_campaign_name.value = '';
    root_campaign_id.value = '';
    affiliate_email.value = '';
    affiliate_id.value = '';
    group_by.value = 'affiliate_id';
    group_col.value = 'affiliate_email';
    sortProp.value = sortProp.value === 'root_campaign_name'
    || sortProp.value === 'campaign_name'
      ? 'affiliate_email'
      : sortProp.value;
  } else if (group_by.value === 'campaign_id') {
    root_campaign_name.value = '';
    root_campaign_id.value = '';
    group_by.value = 'root_campaign_id';
    group_col.value = 'root_campaign_name';
    sortProp.value = sortProp.value === 'campaign_name'
      ? 'root_campaign_name'
      : sortProp.value;
  } else if (group_by.value === 'root_campaign_id') {
    affiliate_email.value = '';
    affiliate_id.value = '';
    group_by.value = 'affiliate_id';
    sortProp.value = sortProp.value === 'root_campaign_name'
      ? 'affiliate_email'
      : sortProp.value;
    group_col.value = 'affiliate_email';
  }

  await nextTick();
  await fetchData();
}

function handlerDatePicker(range: any[], period: string) {
  if (!range && isDataLoading.value) {
    dateRange.value = [];
    return;
  }

  isDataLoading.value = true;

  datePeriod.value = range ? period : '';
  ls.set(`${LS_PATH}/dateperiod`, datePeriod.value);

  dateRange.value = range ? _.cloneDeep(range) : [];
  ls.set(`${LS_PATH}/daterange`, JSON.stringify(dateRange.value));
  page.value = 1;
  fetchData();

  _completedFilterUrl();
}

function handleCurrentRowClick(row: MediaBuyingReportRow) {
  if (_.isEqual(selectedRow.value, row)) {
    selectedRow.value = {} as MediaBuyingReportRow;
    if (dataTableRef) {
      dataTableRef.value.setCurrentRow();
    }

    disableDrilldown.value = true;
  } else {
    selectedRow.value = row;
    if (dataTableRef.value) {
      dataTableRef.value.setCurrentRow(row);
    }
    disableDrilldown.value = false;
  }

  if (dataTableRef.value) {
    dataTableRef.value.changeRowCheck(row);
    dataTableRef.value.clearCheck();
  }

  setTimeout(() => {
    customCurrentRow();
  });
}

function customCurrentRow() {
  const a = document.querySelectorAll('.el-table__row');

  a.forEach((e) => {
    if (e.querySelector('.el-checkbox.is-checked')) {
      e.classList.add('cur');
      e.classList.remove('current-row');
    } else {
      e.classList.remove('cur');
    }
  });
  /* добавил проверка для хрома, т.к. иногда срабатывает листенет
    неодевкатно когда кликаешь, например devtools
  */
  let flag = false;
  a.forEach((e) => {
    if (e.querySelector('.el-checkbox.is-checked')) {
      flag = true;
    }
  });
}

const _query = () => {
  return {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    group_by: group_by.value,
    affiliate_id: affiliate_id.value,
    root_campaign_id: root_campaign_id.value,
    campaign_id: campaign_id.value,
    currency_code: currencyCode.value,
    ...!_.isEmpty(dateRange.value) && {
      from: format(dateRange.value[0], 'yyyy-MM-dd'),
      to: format(dateRange.value[1], 'yyyy-MM-dd'),
    },
  } as GetMediaBuyingReportsSummaryData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset']),
    period: datePeriod.value,
    affiliate_id: affiliate_id.value,
    root_campaign_id: root_campaign_id.value,
    campaign_id: campaign_id.value,
    affiliate_email: affiliate_email.value,
    root_campaign_name: root_campaign_name.value,
    campaign_name: campaign_name.value,
    currency_code: currencyCode.value,
  };

  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getMediaBuyingReports(options);

    count.value = misc!.count;
    currencyCode.value = misc!.currency_code;
    tableData.value = payload.data;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }

    selectedRow.value = {} as MediaBuyingReportRow;
    handleCurrentRowClick({} as MediaBuyingReportRow);
  }
}

function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  const init = (data: any) => {
    setPage(data);

    delete data.page;
    delete data.limit;

    setSort(_urlData);
    setSearch(_urlData);

    const curPeriod = data.period && getPickerReportOptions()
      .find((el) => el.value.short_title === data.period);

    if (curPeriod) {
      dateRange.value = curPeriod.value.range;
      datePeriod.value = data.period;
    } else {
      dateRange.value = data.from && data.to ? [data.from, data.to] : [];
      datePeriod.value = '';
    }

    group_by.value = _.get(data, ['group_by']) || group_by.value;
    affiliate_id.value = _.get(data, ['affiliate_id']) || '';
    root_campaign_id.value = _.get(data, ['root_campaign_id']) || '';
    campaign_id.value = _.get(data, ['campaign_id']) || '';
    affiliate_email.value = _.get(data, ['affiliate_email']) || '';
    root_campaign_name.value = _.get(data, ['root_campaign_name']) || '';
    campaign_name.value = _.get(data, ['campaign_name']) || '';
    currencyCode.value = checkCurrencyCode(_.get(data, 'currency_code'));
  };

  if (!isEmpty(_urlData)) {
    init(_urlData);

    if (group_by.value === 'campaign_id') {
      group_col.value = 'campaign_name';
    }
    if (group_by.value === 'root_campaign_id') {
      group_col.value = 'root_campaign_name';
    }
    if (group_by.value === 'affiliate_id') {
      group_col.value = 'affiliate_email';
    }
  } else {
    let from = ls.get(`${LS_PATH}/daterange`)?.from;
    let to = ls.get(`${LS_PATH}/daterange`)?.to;
    let period = ls.get(`${LS_PATH}/dateperiod`);

    const nosort = ['root_campaign_name', 'campaign_name'];
    if (nosort.includes(sortProp.value)) {
      sortProp.value = 'affiliate_email';
    }

    init({
      from,
      to,
      period,
    });
  }

  nextTick(() => {
    fetchData();
  });
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="media-buying-reports">
    <div class="t:px-32">
      <UiLabel
        :label="$t('theHeader.menu.media-buying.summary_reports')"
        bold
        :font-size="24"
        class="t:mt-16"
      />
      <div
        v-if="affiliate_email"
        class="t:flex t:items-center t:mt-8 t:space-x-8"
      >
        <div
          class="media-buying-reports__drilldown-line-all"
          @click="clearDrill('all')"
        >
          {{ $t('theHeader.menu.media-buying.summary_reports') }}
        </div>
        <UiIcon
          :size="10"
          color="var(--text-body-secondary)"
          name="chevron-right"
        />
        <div class="media-buying-reports__drilldown-line-item">
          <UiIcon
            :size="10"
            color="var(--text-body-secondary)"
            class="t:mr-4"
            name="user"
          />
          <div
            v-html="$t('mediaBuying.reports_table_title.fields.affiliate_email_drilldown.title', {affiliate: affiliate_email })"
          />
          <UiIcon
            v-if="!root_campaign_name"
            color="var(--text-negative)"
            class="t:ml-8"
            name="times"
            cursor
            @click="clearDrill"
          />
        </div>
        <template
          v-if="root_campaign_name"
        >
          <UiIcon
            :size="10"
            color="var(--text-body-secondary)"
            name="chevron-right"
          />
          <div class="media-buying-reports__drilldown-line-item">
            <UiIcon
              :size="10"
              color="var(--text-body-secondary)"
              class="t:mr-4"
              name="globe"
            />
            <div
              v-html="$t('mediaBuying.reports_table_title.fields.root_campaign_name_drilldown.title', {name: root_campaign_name })"
            />
            <UiIcon
              color="var(--text-negative)"
              class="t:ml-8"
              name="times"
              cursor
              @click="clearDrill"
            />
          </div>
        </template>
      </div>
      <UiToolbar>
        <template v-slot:left-slot>
          <UiDatePicker
            v-model="dateRange"
            :disabled="isDataLoading"
            :preset-dates-factory="getPickerReportOptions"
            :placeholder="$t('reports.other.start_end_date')"
            :shortcut-name="datePeriod"
            custom-input
            @update:model-value="handlerDatePicker"
          />
          <UiInput
            v-model.trim="searchQuery"
            :width="180"
            is-search
            @search="handleRefresh"
          />
        </template>
        <template v-slot:right-slot>
          <UiButton
            v-if="!root_campaign_name"
            icon="random"
            :disabled="disableDrilldown"
            @click="handlerDrilldown"
          >
            {{ $t('reports.other.drilldown') }}
          </UiButton>
          <UiCurrencyPicker
            :model-value="currencyCode"
            :currencies="customerCurrencies"
            @update:model-value="changeCurrency"
          />
          <UiPagination
            :page="page"
            :page-size="limit"
            :count="count"
            show-size-select
            @page-change="pageChange"
          />
        </template>
        <template v-slot:actions>
          <UiButton
            icon="sync-alt"
            :disabled="isDataLoading"
            @click="fetchData()"
          >
            {{ $t('reports.other.refresh') }}
          </UiButton>
        </template>
      </UiToolbar>
    </div>
    <UiTableNew
      ref="dataTableRef"
      v-loading="isDataLoading"
      class="media-buying-reports__table"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :currency="currencyCode"
      :sort="{
        prop: sortProp,
        order: sortOrder
      }"
      lazy-loading
      i18n-path="mediaBuying.reports_table_title"
      :row-selectable="!root_campaign_name"
      :select-row="!root_campaign_name"
      :padding="0"
      :column-checkbox-width="1"
      :switch-default-check="false"
      checked-row
      :checked-row-id="group_by"
      @row-click="handleCurrentRowClick"
      @sort="handleSort"
    />
  </div>
</template>

<style lang="scss">
.media-buying-reports{
  &__table {
    .ui-checkbox-group {
      display: none;
    }
  }
  &__drilldown-line-all {
    color: var(--text-link);
    font-size: 12px;
    cursor: pointer;
  }

  &__drilldown-line-item {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 4px;
    background: var(--background-surface);
    height: 22px;
    color: var(--text-body);
    font-size: 12px;

    span {
      font-weight: 600;
    }
  }
}
</style>
